import React, { useState } from "react";
import { Card, Button, Spinner, Badge, Form } from "react-bootstrap";
import Swal from "sweetalert2";
import {
  useCloturerAnneeScolaireMutation,
  useMigrateAnneeScolaireMutation,
} from "features/anneeScolaire/anneeScolaireSlice";
import { useCreateBackupMutation } from "features/backups/backupsSlice";

interface Props {
  currentAnnee: any;
}

const STEPS = [
  { label: "Vérification", icon: "ri-checkbox-circle-line", desc: "Vérifier que les données sont prêtes" },
  { label: "Sauvegarde", icon: "ri-hard-drive-2-line", desc: "Backup automatique pré-clôture" },
  { label: "Archivage", icon: "ri-archive-line", desc: "Clôture et archivage des données" },
  { label: "Nouvelle année", icon: "ri-calendar-check-line", desc: "Création de la prochaine année" },
];

const WizardTab: React.FC<Props> = ({ currentAnnee }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [stepStatus, setStepStatus] = useState<Record<number, "pending" | "loading" | "done" | "error">>({});
  const [newAnneLabel, setNewAnneLabel] = useState("");
  const [verifyResult, setVerifyResult] = useState<any>(null);
  const [backupResult, setBackupResult] = useState<any>(null);
  const [clotureResult, setClotureResult] = useState<any>(null);

  const [migrateAnneeScolaire] = useMigrateAnneeScolaireMutation();
  const [createBackup] = useCreateBackupMutation();
  const [cloturerAnneeScolaire] = useCloturerAnneeScolaireMutation();

  if (!currentAnnee) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <i className="ri-error-warning-line fs-1 text-warning"></i>
          <h5 className="mt-3">Aucune année scolaire active</h5>
          <p className="text-muted">Créez d'abord une année scolaire dans l'onglet Historique.</p>
        </Card.Body>
      </Card>
    );
  }

  const handleVerify = async () => {
    setStepStatus((s) => ({ ...s, 0: "loading" }));
    try {
      const res = await migrateAnneeScolaire().unwrap();
      setVerifyResult(res);
      setStepStatus((s) => ({ ...s, 0: "done" }));
    } catch (err: any) {
      setStepStatus((s) => ({ ...s, 0: "error" }));
      Swal.fire("Erreur", err?.data?.message || "Erreur lors de la vérification", "error");
    }
  };

  const handleBackup = async () => {
    setStepStatus((s) => ({ ...s, 1: "loading" }));
    try {
      const res = await createBackup({ label: `cloture_${currentAnnee.label}` }).unwrap();
      setBackupResult(res);
      setStepStatus((s) => ({ ...s, 1: "done" }));
    } catch (err: any) {
      setStepStatus((s) => ({ ...s, 1: "error" }));
      Swal.fire(
        "Erreur de sauvegarde",
        err?.data?.message || "mongodump n'est pas installé sur le serveur. Vous pouvez continuer, mais sans backup automatique.",
        "warning"
      );
    }
  };

  const handleSkipBackup = () => {
    setBackupResult({ skipped: true });
    setStepStatus((s) => ({ ...s, 1: "done" }));
  };

  const handleArchive = async () => {
    if (!newAnneLabel || !/^\d{4}-\d{4}$/.test(newAnneLabel)) {
      Swal.fire("Erreur", "Saisissez la nouvelle année au format YYYY-YYYY", "error");
      return;
    }

    const confirm = await Swal.fire({
      title: "Confirmer la clôture ?",
      html: `<p>Toutes les données de <strong>${currentAnnee.label}</strong> seront archivées.</p>
        <p>La nouvelle année <strong>${newAnneLabel}</strong> sera créée et activée.</p>
        <p class="text-danger small">Cette action est irréversible sans restauration de backup.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Clôturer",
      cancelButtonText: "Annuler",
      confirmButtonColor: "#e6a817",
    });

    if (!confirm.isConfirmed) return;

    setStepStatus((s) => ({ ...s, 2: "loading" }));
    try {
      const res = await cloturerAnneeScolaire({ nouvelle_annee: newAnneLabel }).unwrap();
      setClotureResult(res);
      setStepStatus((s) => ({ ...s, 2: "done", 3: "done" }));
      setActiveStep(3);
    } catch (err: any) {
      setStepStatus((s) => ({ ...s, 2: "error" }));
      Swal.fire("Erreur", err?.data?.message || "Erreur lors de la clôture", "error");
    }
  };

  const canProceed = (step: number) => {
    if (step === 0) return true;
    return stepStatus[step - 1] === "done";
  };

  const isCompleted = stepStatus[3] === "done";

  return (
    <>
      {/* Stepper header */}
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center position-relative px-4">
            <div
              className="position-absolute"
              style={{ top: "20px", left: "80px", right: "80px", height: "2px", background: "var(--bs-border-color)" }}
            />
            {STEPS.map((step, idx) => {
              const status = stepStatus[idx];
              const isActive = activeStep === idx;
              let variant = "light";
              if (status === "done") variant = "success";
              else if (status === "error") variant = "danger";
              else if (status === "loading") variant = "warning";
              else if (isActive) variant = "primary";

              return (
                <div
                  key={idx}
                  className="text-center position-relative"
                  style={{ zIndex: 1, cursor: canProceed(idx) ? "pointer" : "default", minWidth: 120 }}
                  onClick={() => canProceed(idx) && !isCompleted && setActiveStep(idx)}
                >
                  <div
                    className={`rounded-circle d-inline-flex align-items-center justify-content-center bg-${variant} ${variant === "light" ? "text-muted" : "text-white"}`}
                    style={{ width: 40, height: 40 }}
                  >
                    {status === "done" ? (
                      <i className="ri-check-line fs-5"></i>
                    ) : status === "loading" ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <i className={`${step.icon} fs-5`}></i>
                    )}
                  </div>
                  <div className="mt-2">
                    <h6 className={`mb-0 fs-13 ${isActive ? "text-primary" : ""}`}>{step.label}</h6>
                    <small className="text-muted">{step.desc}</small>
                  </div>
                </div>
              );
            })}
          </div>
        </Card.Body>
      </Card>

      {/* Step content */}
      <Card>
        <Card.Body>
          {isCompleted ? (
            <div className="text-center py-4">
              <i className="ri-check-double-line fs-1 text-success"></i>
              <h4 className="mt-3 text-success">Clôture terminée avec succès</h4>
              <p className="text-muted">
                L'année <strong>{currentAnnee.label}</strong> a été archivée.
                La nouvelle année <strong>{newAnneLabel}</strong> est maintenant active.
              </p>
              {clotureResult?.details && clotureResult.details.length > 0 && (
                <div className="mt-3 text-start" style={{ maxWidth: 400, margin: "0 auto" }}>
                  <h6>Résumé de l'archivage :</h6>
                  {clotureResult.details.map((d: any, i: number) => (
                    <div key={i} className="d-flex justify-content-between border-bottom py-1">
                      <span>{d.model}</span>
                      <Badge bg="secondary">{d.count}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : activeStep === 0 ? (
            <div>
              <h5>
                <i className="ri-checkbox-circle-line me-2 text-primary"></i>
                Étape 1 : Vérification et migration
              </h5>
              <p className="text-muted">
                Cette étape vérifie que toutes les données sont correctement rattachées à l'année
                <strong> {currentAnnee.label}</strong> et corrige les enregistrements orphelins.
              </p>
              {verifyResult ? (
                <div className="mt-3">
                  <div className="alert alert-success">
                    <i className="ri-check-line me-1"></i>
                    {verifyResult.message}
                  </div>
                  {verifyResult.details && verifyResult.details.length > 0 && (
                    <ul className="list-unstyled">
                      {verifyResult.details.map((d: any, i: number) => (
                        <li key={i} className="mb-1">
                          <Badge bg="soft-primary" className="me-2">{d.model}</Badge>
                          {d.count} enregistrement{d.count > 1 ? "s" : ""} corrigé{d.count > 1 ? "s" : ""}
                        </li>
                      ))}
                    </ul>
                  )}
                  <Button variant="primary" className="mt-2" onClick={() => setActiveStep(1)}>
                    Continuer <i className="ri-arrow-right-line ms-1"></i>
                  </Button>
                </div>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleVerify}
                  disabled={stepStatus[0] === "loading"}
                >
                  {stepStatus[0] === "loading" ? (
                    <><Spinner animation="border" size="sm" className="me-1" /> Vérification...</>
                  ) : (
                    <><i className="ri-search-eye-line me-1"></i> Lancer la vérification</>
                  )}
                </Button>
              )}
            </div>
          ) : activeStep === 1 ? (
            <div>
              <h5>
                <i className="ri-hard-drive-2-line me-2 text-info"></i>
                Étape 2 : Sauvegarde pré-clôture
              </h5>
              <p className="text-muted">
                Création d'un backup complet de la base de données avant l'archivage.
                Ce backup sera nommé <code>cloture_{currentAnnee.label}</code>.
              </p>
              {backupResult ? (
                <div className="mt-3">
                  {backupResult.skipped ? (
                    <div className="alert alert-warning">
                      <i className="ri-information-line me-1"></i>
                      Sauvegarde ignorée. Vous pouvez continuer sans backup.
                    </div>
                  ) : (
                    <div className="alert alert-success">
                      <i className="ri-check-line me-1"></i>
                      {backupResult.message || "Backup créé avec succès"}
                    </div>
                  )}
                  <Button variant="primary" className="mt-2" onClick={() => setActiveStep(2)}>
                    Continuer <i className="ri-arrow-right-line ms-1"></i>
                  </Button>
                </div>
              ) : (
                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    onClick={handleBackup}
                    disabled={stepStatus[1] === "loading"}
                  >
                    {stepStatus[1] === "loading" ? (
                      <><Spinner animation="border" size="sm" className="me-1" /> Sauvegarde...</>
                    ) : (
                      <><i className="ri-save-line me-1"></i> Créer le backup</>
                    )}
                  </Button>
                  <Button variant="outline-secondary" onClick={handleSkipBackup}>
                    Passer cette étape
                  </Button>
                </div>
              )}
            </div>
          ) : activeStep === 2 ? (
            <div>
              <h5>
                <i className="ri-archive-line me-2 text-warning"></i>
                Étape 3 : Archivage et nouvelle année
              </h5>
              <p className="text-muted">
                Saisissez le label de la nouvelle année scolaire puis lancez la clôture.
                Toutes les données de <strong>{currentAnnee.label}</strong> seront archivées (statut inactif).
              </p>
              <Form.Group className="mb-3" style={{ maxWidth: 300 }}>
                <Form.Label className="fw-semibold">Nouvelle année scolaire</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="YYYY-YYYY (ex: 2027-2028)"
                  value={newAnneLabel}
                  onChange={(e) => setNewAnneLabel(e.target.value)}
                />
              </Form.Group>
              <Button
                variant="warning"
                onClick={handleArchive}
                disabled={stepStatus[2] === "loading" || !newAnneLabel}
              >
                {stepStatus[2] === "loading" ? (
                  <><Spinner animation="border" size="sm" className="me-1" /> Archivage en cours...</>
                ) : (
                  <><i className="ri-archive-line me-1"></i> Clôturer et créer la nouvelle année</>
                )}
              </Button>
            </div>
          ) : null}
        </Card.Body>
      </Card>

      {/* Navigation */}
      {!isCompleted && activeStep > 0 && (
        <div className="mt-3">
          <Button
            variant="light"
            size="sm"
            onClick={() => setActiveStep((s) => s - 1)}
          >
            <i className="ri-arrow-left-line me-1"></i> Retour
          </Button>
        </div>
      )}
    </>
  );
};

export default WizardTab;
